import { useState, useRef, useEffect } from 'react'
import api from '@/lib/api'
import { Search, X, User } from 'lucide-react'

export interface ContactInfo {
  id: number
  client_name: string
  company: string
  email: string
  phone: string
}

interface Props {
  value: string
  onSelect: (contact: ContactInfo) => void
  onClear: () => void
  label?: string
}

export default function ContactSearchSelect({ value, onSelect, onClear, label = 'Client *' }: Props) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<ContactInfo[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const timeout = useRef<NodeJS.Timeout>()

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    if (query.length < 1) {
      setResults([])
      return
    }
    clearTimeout(timeout.current)
    timeout.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await api.get('/clients/search', { params: { q: query } })
        setResults(res.data)
        setOpen(true)
      } catch {
        setResults([])
      }
      setLoading(false)
    }, 300)
    return () => clearTimeout(timeout.current)
  }, [query])

  return (
    <div ref={ref} className="relative">
      <label className="text-sm font-medium text-slate-700 block mb-1">{label}</label>
      {value ? (
        <div className="flex items-center gap-2 w-full px-3 py-2 text-sm border rounded-lg bg-slate-50">
          <User size={14} className="text-red-800 shrink-0" />
          <span className="flex-1 text-slate-900 font-medium">{value}</span>
          <button type="button" onClick={() => { onClear(); setQuery('') }} className="text-slate-400 hover:text-slate-600">
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
          <input
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true) }}
            onFocus={() => { if (results.length) setOpen(true) }}
            placeholder="Type client name..."
            className="w-full pl-8 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
          />
        </div>
      )}
      {open && !value && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 max-h-56 overflow-y-auto">
          {loading ? (
            <div className="px-3 py-3 text-sm text-slate-400 text-center">Searching...</div>
          ) : results.length === 0 && query.length >= 1 ? (
            <div className="px-3 py-3 text-sm text-slate-400 text-center">No contacts found</div>
          ) : (
            results.map(c => (
              <button key={c.id} type="button"
                onClick={() => { onSelect(c); setQuery(''); setOpen(false) }}
                className="w-full px-3 py-2.5 text-left text-sm hover:bg-red-50 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900">{c.client_name}</span>
                  {c.company && <span className="text-slate-500">— {c.company}</span>}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {[c.email, c.phone].filter(Boolean).join(' · ') || 'No contact info'}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
